import { useEffect, useMemo, useState } from 'react';

export interface SvgData {
	height: number;
	inner: string;
	viewBox: string;
	width: number;
}

const cache = new Map<string, SvgData>();

export const useInlineSvg = (url: string | undefined): SvgData | null => {
	const [data, setData] = useState<SvgData | null>(url ? (cache.get(url) ?? null) : null);

	useEffect(() => {
		if (!url) return;
		if (cache.has(url)) {
			setData(cache.get(url)!);
			return;
		}

		fetch(url, { credentials: 'omit', mode: 'cors' })
			.then((res) => res.text())
			.then((text) => {
				const parser = new DOMParser();
				const doc = parser.parseFromString(text, 'image/svg+xml');
				const svg = doc.querySelector('svg');
				if (!svg) return;
				const result: SvgData = {
					height: parseFloat(svg.getAttribute('height') || '0'),
					inner: svg.innerHTML,
					viewBox: svg.getAttribute('viewBox') || '',
					width: parseFloat(svg.getAttribute('width') || '0'),
				};
				cache.set(url, result);
				setData(result);
			})
			.catch(() => {
				// Silently fail — wireframe is non-critical
			});
	}, [url]);

	return data;
};

const SVG_SIZE_REGEXP = /\.(\d+)x(\d+)\.svg/;

/** Extract original dimensions from the SVG filename (e.g. "hatchback-front.735x464.svg") */
export const useOriginalSize = (url: string | undefined): { origH: number; origW: number } | null =>
	useMemo(() => {
		if (!url) return null;
		const match = SVG_SIZE_REGEXP.exec(url);
		if (!match) return null;
		return { origH: Number(match[2]), origW: Number(match[1]) };
	}, [url]);
